import React, {ReactElement, useEffect, useState} from 'react';
import {IntlProvider} from 'react-intl';
import {Cookies, CookiesProvider} from 'react-cookie';
import DeviceContext, {useDevice} from './DeviceContext';
import LocaleContext, {Locale} from './LocaleContext';
import {IdentityContextProvider} from './IdentityContext';
import useHandleFoid from '../useHandleFoid';
import useHandleLandingUrl from '../useHandleLandingUrl';
import Modal from './ModalContextProvider';
import i18n from '../../i18n';
import {defaultRichTextElements} from '../../i18n/formatters';
import IncludeTermsModal from '../components/TermsModal/IncludeTermsModal';
import {cookieDomain, DEFAULT_LOCALE, hasSecureCookie, LANGUAGE_COOKIE_NAME, UPDATE_LANG_EVENT} from '../constants';
import LanguageBanner from './LanguageBanner/LanguageBanner';
import getFlags from '../flagGetter';
import {isValidLocale} from '../localeUtils/isValidLocale';

export interface SiteContextProviderProps {
  children?: ReactElement;
  pageContext: {
    locale: Locale;
  };
  language: Locale;
}

const SiteContextProvider = ({
  children,
  pageContext: {locale = DEFAULT_LOCALE},
  language = DEFAULT_LOCALE,
}: SiteContextProviderProps): ReactElement => {
  const cookies = new Cookies();
  const device = useDevice();
  const [currentLang, setLang] = useState<Locale>(language);
  const FLAGS = getFlags();
  useHandleFoid();
  useHandleLandingUrl();

  useEffect(() => {
    const changeLang: EventListener = ({
      detail: newLang,
    }: CustomEventInit<Locale>) => {
      if (FLAGS.CONFIGURABLE_LANGUAGE && newLang && isValidLocale(newLang)) {
        setLang(newLang);
        cookies.set(LANGUAGE_COOKIE_NAME, newLang, {
          path: '/',
          domain: cookieDomain,
          secure: hasSecureCookie,
        });
      }
    };

    window.addEventListener(UPDATE_LANG_EVENT, changeLang);

    // teardown
    return () => {
      window.removeEventListener(UPDATE_LANG_EVENT, changeLang);
    };
  });

  // set Identity first, so we can pull locale/etc out of user object later
  return (
    <CookiesProvider>
      <IntlProvider
        key={currentLang}
        locale={currentLang}
        messages={i18n(currentLang)}
        defaultLocale={DEFAULT_LOCALE}
        defaultRichTextElements={defaultRichTextElements}
      >
        <LocaleContext.Provider value={locale}>
          <IdentityContextProvider>
            <DeviceContext.Provider value={device}>
              <Modal>
                {/* Reliant on modal context so must be included here to work globally */}
                <>
                  <IncludeTermsModal />
                  {children}
                </>
              </Modal>
            </DeviceContext.Provider>
          </IdentityContextProvider>
        </LocaleContext.Provider>
        {FLAGS.CONFIGURABLE_LANGUAGE && <LanguageBanner lang={currentLang} />}
      </IntlProvider>
    </CookiesProvider>
  );
};

export default SiteContextProvider;
